import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

import { firstValueFrom, of, pipe } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ApplicationService, BoxService } from '../services';
import { BoxState, initialBoxState } from '../models/state/box.state';
import { Box, BoxPageRequest, BoxUpdateRequest } from '../models';
import { SelectedCustomerStore } from './selected-customer.store';

export const CustomerBoxStore = signalStore(
  { providedIn: 'root' },
  withProps(() => {
    let customerSignal = inject(SelectedCustomerStore)?.selectedCustomer;
    return { customer: computed(() => customerSignal()) };
  }),
  withState<BoxState>(initialBoxState),
  withComputed(({ boxesStatistics }) => ({
    metrics: computed(() => [
      {
        label: 'boxes.metrics.totalBoxes',
        value: boxesStatistics().totalCount,
        icon: 'storage',
        iconSize: 1.375,
      },
      {
        label: 'boxes.metrics.connectedBoxes',
        value: boxesStatistics().connectedCount,
        icon: 'power_settings_new',
        iconSize: 1.375,
      },
      {
        label: 'boxes.metrics.disconnectedBoxes',
        value: boxesStatistics().totalCount - boxesStatistics().connectedCount,
        icon: 'do_not_disturb_on',
        iconSize: 1.375,
      },
    ]),
  })),
  withMethods((store, applicationService = inject(ApplicationService), boxService = inject(BoxService)) => ({
    loadBoxes: rxMethod<BoxPageRequest>(
      pipe(
        map((_pageRequest) => {
          const pageRequest = { ..._pageRequest, page: 0, size: applicationService.itemsPerPage() };
          patchState(store, { loading: true, pageRequest });
          return pageRequest;
        }),
        switchMap((pageRequest) =>
          boxService.getCustomerBoxes(store.customer()?.id, pageRequest).pipe(
            tap((boxes) => patchState(store, { boxes })),
            catchError(() => of()),
            finalize(() => patchState(store, { loading: false })),
          ),
        ),
      ),
    ),
    loadMoreBoxes: rxMethod<void>(
      pipe(
        map(() => {
          const { page, ...restOfPageRequest } = store.pageRequest();
          const pageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
          patchState(store, { pageRequest });
          return pageRequest;
        }),
        concatMap((pageRequest) =>
          boxService.getCustomerBoxes(store.customer()?.id, pageRequest).pipe(
            tap((boxes) => patchState(store, { boxes: [...store.boxes(), ...boxes] })),
            catchError(() => of()),
          ),
        ),
      ),
    ),
    loadBoxesStatistics: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { statisticsLoading: true })),
        switchMap(() =>
          boxService.getCustomerBoxesStatistics(store.customer()?.id).pipe(
            tap((boxesStatistics) => patchState(store, { boxesStatistics })),
            catchError(() => of()),
            finalize(() => patchState(store, { statisticsLoading: false })),
          ),
        ),
      ),
    ),
  })),
  withMethods(
    (
      store,
      boxService = inject(BoxService),
      toastrService = inject(ToastrService),
      translateService = inject(TranslateService),
    ) => ({
      deleteBox: rxMethod<string>(
        pipe(
          switchMap((id) =>
            boxService.deleteCustomerBox(store.customer()?.id, id).pipe(
              tap(() => {
                patchState(store, {
                  boxes: store.boxes().filter((b) => b.id !== id),
                });
                toastrService.success(translateService.instant('boxes.alerts.deleteSuccess'));
                store.loadBoxesStatistics();
              }),
              catchError(() => {
                toastrService.error(translateService.instant('boxes.alerts.deleteFail'));
                return of();
              }),
            ),
          ),
        ),
      ),
      async updateBox(id: string, boxData: BoxUpdateRequest): Promise<Box> {
        patchState(store, { saving: true });

        try {
          const response = await firstValueFrom(boxService.updateCustomerBox(store.customer()?.id, id, boxData));

          patchState(store, {
            boxes: [...store.boxes().map((b) => (b.id === id ? { ...b, ...response } : b))],
          });

          toastrService.success(translateService.instant('boxes.alerts.updateSuccess'));
          return response;
        } catch (err) {
          toastrService.error(translateService.instant('boxes.alerts.updateFail'));
          throw err;
        } finally {
          patchState(store, { saving: false });
        }
      },
    }),
  ),
);
